import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import {
  getCollectionsList, exportCollectionJson, exportCollectionExcel,
  exportBackupJson, exportBackupExcel, getBackupHistory,
} from '../../services/api';
import { DownloadSimple, UploadSimple, Database, FileXls, FileJs, ArrowClockwise, CheckCircle, Clock, MagicWand } from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import ImportWizard from '../../components/ImportWizard';

function downloadBlob(response, fallbackName) {
  const disposition = response.headers['content-disposition'] || '';
  const match = disposition.match(/filename="?([^"]+)"?/);
  const filename = match ? match[1] : fallbackName;
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export function ExportImportSettings() {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState({});
  const [history, setHistory] = useState([]);
  const [exportAllLoading, setExportAllLoading] = useState('');
  const [wizardOpen, setWizardOpen] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [colsRes, histRes] = await Promise.all([getCollectionsList(), getBackupHistory()]);
      setCollections(colsRes.data.collections || []);
      setHistory(histRes.data.items || []);
    } catch {} finally { setLoading(false); }
  };

  const handleExportJson = async (col) => {
    setExporting(prev => ({ ...prev, [`${col}_json`]: true }));
    try {
      const res = await exportCollectionJson(col);
      downloadBlob(res, `${col}.json`);
      toast.success(`Export JSON ${col} reussi`);
    } catch { toast.error('Erreur export'); }
    finally { setExporting(prev => ({ ...prev, [`${col}_json`]: false })); }
  };

  const handleExportExcel = async (col) => {
    setExporting(prev => ({ ...prev, [`${col}_xlsx`]: true }));
    try {
      const res = await exportCollectionExcel(col);
      downloadBlob(res, `${col}.xlsx`);
      toast.success(`Export Excel ${col} reussi`);
    } catch { toast.error('Erreur export'); }
    finally { setExporting(prev => ({ ...prev, [`${col}_xlsx`]: false })); }
  };

  const handleExportAll = async (format) => {
    setExportAllLoading(format);
    try {
      const res = format === 'json' ? await exportBackupJson() : await exportBackupExcel();
      downloadBlob(res, format === 'json' ? 'crm_backup.zip' : 'crm_export.xlsx');
      toast.success(`Export complet ${format.toUpperCase()} reussi`);
    } catch { toast.error('Erreur export'); }
    finally { setExportAllLoading(''); }
  };

  const totalRecords = collections.reduce((sum, c) => sum + c.count, 0);

  return (
    <div className="space-y-6" data-testid="export-import-settings">
      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="p-3 rounded-lg border border-[#E8E6E1] bg-[#F9F8F5]">
          <p className="text-[10px] text-[#5E6D66] uppercase tracking-wider">Total enregistrements</p>
          <p className="text-lg font-bold text-[#1A2421] mt-1" data-testid="total-records">{loading ? '-' : totalRecords.toLocaleString('fr-FR')}</p>
        </div>
        <div className="p-3 rounded-lg border border-[#E8E6E1] bg-[#F9F8F5]">
          <p className="text-[10px] text-[#5E6D66] uppercase tracking-wider">Modules</p>
          <p className="text-lg font-bold text-[#1A2421] mt-1">{collections.length}</p>
        </div>
        <div className="p-3 rounded-lg border border-[#E8E6E1] bg-[#F9F8F5]">
          <p className="text-[10px] text-[#5E6D66] uppercase tracking-wider">Formats</p>
          <p className="text-sm font-medium text-[#1A2421] mt-1">JSON & Excel</p>
        </div>
        <div className="p-3 rounded-lg border border-[#E8E6E1] bg-[#F9F8F5]">
          <p className="text-[10px] text-[#5E6D66] uppercase tracking-wider">Derniere operation</p>
          <p className="text-sm font-medium text-[#1A2421] mt-1">{history[0] ? new Date(history[0].timestamp).toLocaleDateString('fr-FR') : '-'}</p>
        </div>
      </div>

      {/* Export complet */}
      <Card className="border-[#E8E6E1]">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
            <Database size={18} className="text-[#2D5A4C]" />
            Export complet (toutes les donnees)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3">
            <Button onClick={() => handleExportAll('json')} disabled={!!exportAllLoading} className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="export-all-json-btn">
              {exportAllLoading === 'json' ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileJs size={16} className="mr-2" />}
              Export JSON (ZIP)
            </Button>
            <Button onClick={() => handleExportAll('excel')} disabled={!!exportAllLoading} variant="outline" className="border-[#2D5A4C] text-[#2D5A4C]" data-testid="export-all-excel-btn">
              {exportAllLoading === 'excel' ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileXls size={16} className="mr-2" />}
              Export Excel
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Export par module */}
      <Card className="border-[#E8E6E1]">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <DownloadSimple size={18} className="text-[#2D5A4C]" />
              Export par module
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={fetchData} className="text-[#5E6D66]"><ArrowClockwise size={14} /></Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-6"><Loader2 className="h-6 w-6 animate-spin text-[#2D5A4C]" /></div>
          ) : (
            <div className="space-y-2" data-testid="export-modules-list">
              {collections.map((col) => (
                <div key={col.key} className="flex items-center justify-between p-3 rounded-lg border border-[#E8E6E1] hover:bg-[#F9F8F5] transition-colors" data-testid={`export-row-${col.key}`}>
                  <div className="flex items-center gap-3">
                    <div>
                      <p className="text-sm font-medium text-[#1A2421]">{col.label}</p>
                      <p className="text-[11px] text-[#5E6D66]">{col.count.toLocaleString('fr-FR')} enregistrements</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => handleExportJson(col.key)} disabled={exporting[`${col.key}_json`] || col.count === 0} className="text-xs h-8" data-testid={`export-json-${col.key}`}>
                      {exporting[`${col.key}_json`] ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <FileJs size={14} className="mr-1" />}
                      JSON
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleExportExcel(col.key)} disabled={exporting[`${col.key}_xlsx`] || col.count === 0} className="text-xs h-8" data-testid={`export-excel-${col.key}`}>
                      {exporting[`${col.key}_xlsx`] ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <FileXls size={14} className="mr-1" />}
                      Excel
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Import avance */}
      <Card className="border-[#D47A62]/30 bg-[#D47A62]/3">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
            <UploadSimple size={18} className="text-[#D47A62]" />
            Import avance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-[#5E6D66] mb-3">
            Importez des donnees depuis un fichier JSON ou Excel avec mapping de colonnes, validation et detection de doublons.
          </p>
          <Button
            onClick={() => setWizardOpen(true)}
            className="bg-[#D47A62] hover:bg-[#C06A52]"
            data-testid="open-import-wizard-btn"
          >
            <MagicWand size={16} className="mr-2" />
            Ouvrir l'assistant d'import
          </Button>
        </CardContent>
      </Card>

      {/* Import Wizard Dialog */}
      <Dialog open={wizardOpen} onOpenChange={setWizardOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="import-wizard-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <MagicWand size={20} className="text-[#D47A62]" />
              Assistant d'import
            </DialogTitle>
            <DialogDescription className="text-xs text-[#5E6D66]">
              Importez vos donnees en 4 etapes : fichier, mapping, validation, resultat.
            </DialogDescription>
          </DialogHeader>
          <ImportWizard
            collections={collections}
            onComplete={() => { setWizardOpen(false); fetchData(); }}
            onCancel={() => setWizardOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* History */}
      {history.length > 0 && (
        <Card className="border-[#E8E6E1]">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <Clock size={18} className="text-[#5E6D66]" />
              Historique des operations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2" data-testid="backup-history">
              {history.slice(0, 10).map((h, idx) => (
                <div key={h.id || idx} className="flex items-center gap-3 p-2.5 rounded-lg bg-[#F9F8F5] text-xs">
                  <Badge className={h.operation.includes('export') ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'} variant="secondary">
                    {h.operation.includes('export') ? 'Export' : 'Import'}
                  </Badge>
                  <Badge variant="outline" className="text-[10px]">
                    {h.operation.includes('json') ? 'JSON' : 'Excel'}
                  </Badge>
                  <span className="text-[#1A2421] font-medium">{h.total} enregistrements</span>
                  <span className="text-[#5E6D66]">par {h.user_name || h.user_email}</span>
                  <span className="ml-auto text-[10px] text-[#8DB0A1]">
                    {h.timestamp ? new Date(h.timestamp).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
